/**
 * Optimized Query Hook for Sentient CRM
 * Provides performance-optimized React Query configurations for startup speed
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import React, { useCallback, useMemo, useEffect } from 'react';

// Performance-optimized query configurations
export const QUERY_CONFIGS = {
  // Dashboard data - refresh frequently but cache aggressively
  dashboard: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  },

  // Contact/Deal lists - balance freshness with performance
  lists: {
    staleTime: 1 * 60 * 1000, // 1 minute
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    keepPreviousData: true, // For smooth pagination
  },

  // Detail pages - cache longer since they change less frequently
  details: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  },

  // Static/reference data - cache very aggressively
  static: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  },

  // Real-time data - minimal caching
  realtime: {
    staleTime: 0,
    cacheTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
    refetchInterval: 30 * 1000, // 30 seconds
  }
};

export type QueryConfigType = keyof typeof QUERY_CONFIGS;

/**
 * Optimized query hook with performance configurations
 */
export function useOptimizedQuery<TData = unknown, TError = unknown>(
  queryKey: unknown[],
  queryFn: () => Promise<TData>,
  configType: QueryConfigType = 'lists',
  customOptions?: Partial<UseQueryOptions<TData, TError>>
): UseQueryResult<TData, TError> {

  const optimizedOptions = useMemo(() => ({
    queryKey,
    queryFn,
    ...QUERY_CONFIGS[configType],
    ...customOptions,
    // Performance optimizations
    retry: (failureCount: number, error: any) => {
      // Don't retry on 4xx errors, only on network/5xx errors
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false;
      }
      return failureCount < 2; // Max 2 retries
    },
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
  }), [queryKey, queryFn, configType, customOptions]);

  return useQuery(optimizedOptions);
}

/**
 * Optimized dashboard query hook
 */
export function useOptimizedDashboard() {
  const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
  return useOptimizedQuery(
    ['dashboard', 'optimized'],
    async () => {
      // Get Firebase token from auth or fallback to stored tokens
      const { auth } = await import('../config/firebase');
      let token = null;
      
      // Try to get Firebase token first
      if (auth.currentUser) {
        try {
          token = await auth.currentUser.getIdToken();
        } catch (error) {
          console.error('Error getting Firebase token:', error);
        }
      }
      
      // Fallback to stored tokens
      if (!token) {
        token = localStorage.getItem('firebaseToken') || localStorage.getItem('auth_token');
      }
      
      const response = await fetch(`${API_BASE_URL}/dashboard/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    },
    'dashboard'
  );
}

/**
 * Optimized contacts query with pagination
 */
export function useOptimizedContacts(
  page: number = 0,
  limit: number = 50,
  search?: string
) {
  const queryKey = useMemo(() =>
    ['contacts', 'optimized', page, limit, search].filter(Boolean),
    [page, limit, search]
  );

  return useOptimizedQuery(
    queryKey,
    () => {
      const params = new URLSearchParams({
        skip: (page * limit).toString(),
        limit: limit.toString(),
        ...(search && { search })
      });
      return fetch(`/api/v1/performance-optimized/contacts/optimized?${params}`)
        .then(res => res.json());
    },
    'lists'
  );
}

/**
 * Optimized deals query with insights
 */
export function useOptimizedDeals(page: number = 0, limit: number = 50) {
  const queryKey = useMemo(() => ['deals', 'optimized', page, limit], [page, limit]);

  return useOptimizedQuery(
    queryKey,
    () => {
      const params = new URLSearchParams({
        skip: (page * limit).toString(),
        limit: limit.toString()
      });
      return fetch(`/api/v1/performance-optimized/deals/optimized?${params}`)
        .then(res => res.json());
    },
    'lists'
  );
}

/**
 * Performance monitoring hook
 */
export function usePerformanceStats() {
  return useOptimizedQuery(
    ['performance', 'stats'],
    () => fetch('/api/v1/performance-optimized/performance/stats').then(res => res.json()),
    'realtime'
  );
}

/**
 * Cache warming utility
 */
export function useCacheWarming() {
  const warmCache = useCallback(async () => {
    try {
      await fetch('/api/v1/performance-optimized/performance/warm-cache', {
        method: 'POST'
      });
      return { success: true };
    } catch (error) {
      console.error('Cache warming failed:', error);
      return { success: false, error };
    }
  }, []);

  return { warmCache };
}

/**
 * Performance optimization utilities
 */
export const PerformanceUtils = {
  /**
   * Debounce function for search inputs
   */
  debounce: <T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): ((...args: Parameters<T>) => void) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  },

  /**
   * Throttle function for scroll events
   */
  throttle: <T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): ((...args: Parameters<T>) => void) => {
    let lastCall = 0;
    return (...args: Parameters<T>) => {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        func(...args);
      }
    };
  },

  /**
   * Lazy load component helper
   */
  createLazyComponent: (importFn: () => Promise<{ default: React.ComponentType<any> }>) => {
    const React = require('react');
    return React.lazy(importFn);
  },

  /**
   * Memoization helper for expensive calculations
   */
  memoize: <T extends (...args: any[]) => any>(fn: T): T => {
    const cache = new Map();
    return ((...args: any[]) => {
      const key = JSON.stringify(args);
      if (cache.has(key)) {
        return cache.get(key);
      }
      const result = fn(...args);
      cache.set(key, result);
      return result;
    }) as T;
  },

  /**
   * Virtual scrolling helper for large lists
   */
  calculateVisibleRange: (
    scrollTop: number,
    containerHeight: number,
    itemHeight: number,
    totalItems: number,
    overscan: number = 5
  ) => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endIndex = Math.min(
      totalItems - 1,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
    );
    return { startIndex, endIndex };
  }
};

/**
 * Performance monitoring hook for components
 */
export function useComponentPerformance(componentName: string) {
  const startTime = useMemo(() => performance.now(), []);

  const recordRender = useCallback(() => {
    const renderTime = performance.now() - startTime;
    if (renderTime > 100) { // Log slow renders (>100ms)
      console.warn(`Slow render detected in ${componentName}: ${renderTime.toFixed(2)}ms`);
    }
  }, [componentName, startTime]);

  // Record render time on mount and updates
  useEffect(() => {
    recordRender();
  });

  return { recordRender };
}
